/**
 * SkillResultsPanel — "Mes résultats" tab content for a specific skill
 * or tajwīd rule. Renders the user's progress row (accuracy %, status,
 * attempts), most recent attempts filtered to this scope, and simple
 * recommendations.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertCircle, RefreshCcw, Sparkles, ArrowRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { coursesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const STATUS = {
  mastered:     { fr: 'Maîtrisé',     cls: 'text-emerald-300 bg-emerald-500/15' },
  learning:     { fr: 'En cours',     cls: 'text-amber-300 bg-amber-500/10' },
  needs_review: { fr: 'À revoir',     cls: 'text-rose-300 bg-rose-500/10' },
  not_started:  { fr: 'Non commencé', cls: 'text-text-muted bg-surface-elevated' },
};

export const SkillResultsPanel = ({
  section,        // 'reading' | 'tajweed'
  skill = null,
  ruleId = null,
  testidPrefix = 'results',
  practiceHref = null,  // link to practice ("Pratiquer dans le Coran")
}) => {
  const { isAuthenticated } = useAuth();
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    coursesApi.myProgress()
      .then((r) => !cancel && setProg(r.data))
      .catch(() => !cancel && setProg(null))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [isAuthenticated, section, skill, ruleId]);

  if (!isAuthenticated) {
    return (
      <div
        className="rounded-lg border border-dashed border-border bg-surface-elevated/20 p-5 text-sm text-text-muted"
        data-testid={`${testidPrefix}-anonymous`}
      >
        Connectez-vous pour voir vos résultats et suivre votre progression.
        <div className="mt-3">
          <Link to="/login">
            <Button size="sm" variant="outline">Se connecter</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-surface-elevated/30 p-5 text-sm text-text-muted"
           data-testid={`${testidPrefix}-loading`}>
        Chargement…
      </div>
    );
  }

  // Find the relevant progress row
  const row = section === 'reading'
    ? (prog?.reading || []).find((s) => s.skill === skill)
    : (prog?.tajweed || []).find((s) => s.ruleId === ruleId);

  const recent = (prog?.recentAttempts || []).filter((a) => {
    if (section !== a.section) return false;
    if (section === 'reading') return a.skill === skill;
    return a.ruleId === ruleId || a.expectedRuleId === ruleId;
  }).slice(0, 5);

  const status = row?.status || 'not_started';
  const meta = STATUS[status] || STATUS.not_started;
  const accPercent = Math.round((row?.accuracy || 0) * 100);

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-panel`}>
      <div
        className="rounded-lg border border-border bg-surface-elevated/40 p-4 space-y-3"
        data-testid={`${testidPrefix}-summary`}
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs uppercase tracking-wider text-text-muted">
              Statut
            </span>
            <span className={`text-[11px] px-2 py-0.5 rounded ${meta.cls}`}
                  data-testid={`${testidPrefix}-status`}>
              {meta.fr}
            </span>
          </div>
          <span className="text-2xl font-serif text-text-primary"
                data-testid={`${testidPrefix}-accuracy`}>
            {accPercent}%
          </span>
        </div>

        <div className="h-1.5 w-full bg-background/60 rounded overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary/70 to-emerald-400/80 transition-all"
            style={{ width: `${accPercent}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            {row?.attempts || 0} tentative{(row?.attempts || 0) > 1 ? 's' : ''}
            {row?.correct != null ? ` · ${row.correct} réussies` : ''}
          </span>
          <Link to="/mes-progres" className="text-primary hover:underline inline-flex items-center">
            Voir tous mes progrès <ArrowRight className="w-3 h-3 ml-1" />
          </Link>
        </div>
      </div>

      {/* Recent attempts */}
      {recent.length > 0 && (
        <div className="rounded-lg border border-border bg-surface-elevated/30 p-4"
             data-testid={`${testidPrefix}-recent`}>
          <h4 className="text-xs uppercase tracking-wider text-text-muted mb-2">
            Tentatives récentes
          </h4>
          <ul className="space-y-1.5 text-xs">
            {recent.map((a) => (
              <li key={a.id} className="flex items-center justify-between border-b border-border/40 pb-1.5">
                <span className="flex items-center gap-2">
                  {a.isCorrect
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    : <AlertCircle className="w-3.5 h-3.5 text-rose-400" />}
                  <span className="text-text-secondary">
                    {a.isCorrect ? 'Bonne réponse' : 'Réponse incorrecte'}
                  </span>
                  {a.exerciseId && (
                    <Badge variant="outline" className="text-[10px]">
                      {a.exerciseId}
                    </Badge>
                  )}
                </span>
                <span className="text-text-muted">
                  {new Date(a.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendation */}
      {row && row.status === 'needs_review' && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200 flex items-start gap-2"
             data-testid={`${testidPrefix}-needs-review`}>
          <RefreshCcw className="w-4 h-4 mt-0.5 shrink-0" />
          <p>
            Cette compétence est à revoir. Relancez une série « Facile » puis « Moyen »
            pour consolider les bases.
          </p>
        </div>
      )}

      {practiceHref && (
        <div className="flex justify-end">
          <Link to={practiceHref}>
            <Button size="sm" variant="outline" data-testid={`${testidPrefix}-practice`}>
              Pratiquer dans le Coran <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default SkillResultsPanel;
